// @flow
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '@shared/Modal/Modal';
import IconCircleCheck from '@icons/components/IconCircleCheck';
import IconCircleXMark from '@icons/components/IconCircleXMark';
import KpiCircularProgress from '@groupClass/components/KpiCircularProgress';

type ExportStatus = 'processing' | 'completed' | 'failed';

type Props = {
  isActive: boolean,
  status: ExportStatus | null,
  downloadUrl: string | null,
  onClose: () => void,
  onRetry: () => void,
};

const ModalKpiExport = (props: Props): React$Node => {
  const { isActive, status, downloadUrl, onClose, onRetry } = props;
  const { t } = useTranslation();

  const [ progress, setProgress ] = useState<number>(0);
  const intervalRef = useRef<IntervalID | null>(null);

  useEffect(() => {
    if (!isActive) {
      setProgress(0);
      return;
    }

    if (status === 'processing') {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => (prev >= 100 ? prev : Math.min(prev + 5, 100)));
      }, 80);
    } else if (status === 'completed') {
      clearInterval(intervalRef.current);
      setProgress(100);
    } else if (status === 'failed') {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [ isActive, status ]);

  useEffect(() => {
    if (status === 'completed' && downloadUrl) {
      setProgress(100);

      const timeoutId = setTimeout(() => {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', '');
        document.body?.appendChild(link);
        link.click();
        link.remove();
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [ status, downloadUrl ]);

  return (
    <Modal
      isActive={ isActive }
      isClipped
      title={ t('groupClasses.kpiExport') }
      footer={ null }
      onClose={ status === 'completed' || status === 'failed' ? onClose : undefined }
      modalContentClassName="modal-kpi-export-card"
    >
      { status === 'failed' ? (
        <div className="kpi-export-state kpi-export-state--error">
          <div className="kpi-export-state__icon kpi-export-state__icon--error">
            <IconCircleXMark />
          </div>
          <h2 className="kpi-export-state__title">{ t('groupClasses.kpiExportModal.errorTitle') }</h2>
          <p className="kpi-export-state__message">{ t('groupClasses.kpiExportModal.errorMessage') }</p>
          <div className="kpi-export-state__actions">
            <button className="kpi-export-btn kpi-export-btn--primary" onClick={ onRetry } type="button">
              { t('groupClasses.kpiExportModal.retry') }
            </button>
          </div>
        </div>
      ) : status === 'completed' ? (
        <div className="kpi-export-state kpi-export-state--success">
          <div className="kpi-export-state__icon kpi-export-state__icon--success">
            <IconCircleCheck />
          </div>
          <h2 className="kpi-export-state__title">{ t('groupClasses.kpiExportModal.successTitle') }</h2>
          <p className="kpi-export-state__message">{ t('groupClasses.kpiExportModal.successMessage') }</p>
        </div>
      ) : (
        <div className="kpi-export-progress">
          <KpiCircularProgress progress={ progress } />
          <p className="kpi-export-progress__subtitle">{ t('groupClasses.kpiExportModal.preparingSubtitle') }</p>
        </div>
      ) }
    </Modal>
  );
};

export default ModalKpiExport;
